import { ReactNode } from 'react';

import {
  SBadge,
  SBullet,
  SBulletIcon,
  SBullets,
  SBulletText,
  SCard,
  SCloseButton,
  SCtaButton,
  SDescription,
  SStrip,
  STitle,
} from './styled';

// `dialog` and `sheet` (the mobile drawer) sit in a modal; `inline` is a card on the page itself.
export type PaywallCardLayout = 'dialog' | 'inline' | 'sheet';

type PaywallCardProps = {
  title: ReactNode;
  badge: ReactNode;
  badgeTone?: 'brand' | 'warning';
  bullets: { id: string; text: ReactNode }[];
  closeLabel?: string;
  ctaLabel: ReactNode;
  description: ReactNode;
  isCtaDisabled?: boolean;
  layout: PaywallCardLayout;
  // The visual between the description and the bullets: it brings its own height and margins.
  preview?: ReactNode;
  strip: ReactNode;
  stripTallLine?: boolean;
  // The card's test id; its parts get it as a prefix (`<testId>-cta`, `<testId>-badge`, ...).
  testId: string;
  onClose?: () => void;
  onCta: () => void;
};

const BulletCheckIcon = () => (
  <SBulletIcon fill="none" height={19} viewBox="0 0 24 24" width={19}>
    <path
      d="M5 12.5l4.5 4.5L19 7"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.2"
    />
  </SBulletIcon>
);

// A paywall in the dashboard style: badge, title, description, a visual, ticked benefits, a framed line and one CTA.
export const PaywallCard = ({
  title,
  badge,
  badgeTone = 'brand',
  bullets,
  closeLabel,
  ctaLabel,
  description,
  isCtaDisabled = false,
  layout,
  preview,
  strip,
  stripTallLine = false,
  testId,
  onClose,
  onCta,
}: PaywallCardProps) => (
  <SCard $layout={layout} data-testid={testId}>
    {onClose ? (
      <SCloseButton aria-label={closeLabel} data-testid={`${testId}-close`} type="button" onClick={onClose}>
        <svg fill="none" height={18} viewBox="0 0 24 24" width={18}>
          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
        </svg>
      </SCloseButton>
    ) : null}

    <SBadge $tone={badgeTone} data-testid={`${testId}-badge`}>
      {badge}
    </SBadge>

    <STitle $layout={layout} data-testid={`${testId}-title`}>
      {title}
    </STitle>
    <SDescription data-testid={`${testId}-description`}>{description}</SDescription>

    {preview}

    <SBullets data-testid={`${testId}-bullets`}>
      {bullets.map(({ id, text }) => (
        <SBullet key={id}>
          <BulletCheckIcon />
          <SBulletText>{text}</SBulletText>
        </SBullet>
      ))}
    </SBullets>

    <SStrip $tallLine={stripTallLine} data-testid={`${testId}-strip`}>
      {strip}
    </SStrip>

    <SCtaButton data-testid={`${testId}-cta`} disabled={isCtaDisabled} variant="primary" fullWidth onClick={onCta}>
      {ctaLabel}
    </SCtaButton>
  </SCard>
);
